// Hooks every data-backed XOmics screen uses instead of running its own timer.
//
// useLiveTopics   -- run a callback when any of several topics is invalidated.
// useLiveResource -- the common case: fetch once, refetch when a topic changes.
//
// Why there is still a poll: the socket is the fast path, not the only path. A
// long reconciliation interval covers what a socket cannot -- a signal dropped
// mid-reconnect, a suspended laptop, a proxy idling the connection out. A
// dropped socket degrades to "refreshes less often", never to "shows stale data
// forever".

import { useCallback, useEffect, useRef, useState } from 'react';

import { subscribeTopic } from './liveUpdates';
import { errorMessage } from './ui';

const DEFAULT_RECONCILE_MS = 300000;

/** Runs `onInvalidate` whenever any of `topics` changes. */
export function useLiveTopics(topics, onInvalidate) {
  const handler = useRef(onInvalidate);
  handler.current = onInvalidate;
  const key = topics.join(',');
  useEffect(() => {
    const offs = key.split(',').filter(Boolean)
      .map((topic) => subscribeTopic(topic, () => handler.current()));
    return () => offs.forEach((off) => off());
  }, [key]);
}

/**
 * Fetch once, then refetch whenever any of `topics` is invalidated.
 *
 * `load` must be stable (useCallback) -- it is the dependency that triggers a
 * refetch when its own inputs change.
 */
export function useLiveResource(topics, load, options = {}) {
  const { reconcileMs = DEFAULT_RECONCILE_MS, enabled = true } = options;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);

  // Guards against a slow earlier response overwriting a newer one.
  const generation = useRef(0);
  const mounted = useRef(true);
  // Raised on mount, not only lowered on unmount: StrictMode mounts, unmounts
  // and mounts again, and with only the cleanup the second mount begins with
  // this already false, every guarded setState is skipped, and the screen loads
  // forever.
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const run = useCallback(async (showLoading) => {
    if (!enabled) return;
    const mine = generation.current + 1;
    generation.current = mine;
    if (showLoading) setLoading(true);
    try {
      const next = await load();
      if (!mounted.current || mine !== generation.current) return;
      setData(next);
      setError(null);
    } catch (caught) {
      if (!mounted.current || mine !== generation.current) return;
      setError(errorMessage(caught));
    } finally {
      if (mounted.current && mine === generation.current) setLoading(false);
    }
  }, [enabled, load]);

  useEffect(() => { run(true); }, [run]);

  useLiveTopics(topics, () => { run(false); });

  useEffect(() => {
    if (!enabled || reconcileMs <= 0) return undefined;
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      run(false);
    }, reconcileMs);
    return () => window.clearInterval(timer);
  }, [enabled, reconcileMs, run]);

  const refresh = useCallback(() => run(false), [run]);
  return { data, loading, error, refresh };
}
